// src/lib/utils.ts
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, startOfWeek, endOfWeek, addDays } from "date-fns";

// Utility for merging Tailwind classes
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Format currency
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

// Format date to display format
export function formatDisplayDate(date: Date): string {
  return format(date, "dd MMM yyyy");
}

// Format date for API
export function formatAPIDate(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

// Get week dates (Monday to Friday)
export function getWeekDates(date: Date) {
  const start = startOfWeek(date, { weekStartsOn: 1 }); // Monday
  const weekDays: Date[] = [];
  
  for (let i = 0; i < 5; i++) { // Monday to Friday (5 days)
    weekDays.push(addDays(start, i));
  }
  
  return weekDays;
}

// Format hours for display
export function formatHours(hours: number | null): string {
  if (hours === null) return "-";
  return hours.toFixed(2);
}

// Calculate total hours for a week
export function calculateTotalHours(hours: { [key: string]: number | null }): number {
  return Object.values(hours)
    .filter((hour): hour is number => hour !== null)
    .reduce((total, hour) => total + hour, 0);
}

// Validate hours input
export function validateHours(hours: number): boolean {
  return hours >= 0 && hours <= 24;
}

// Format file size
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

// Truncate text with ellipsis
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

// Download blob as file
export function downloadBlob(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// Format timesheet status for display
export function formatStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

// Check if user is admin
export function isAdmin(userRole?: string): boolean {
  return userRole === "admin";
}

// Generate timesheet week label
export function getWeekLabel(date: Date): string {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  const end = endOfWeek(date, { weekStartsOn: 1 });
  return `${format(start, "dd MMM")} - ${format(end, "dd MMM yyyy")}`;
}

// Parse safe JSON
export function safeJSONParse<T>(json: string, fallback: T): T {
  try {
    return JSON.parse(json);
  } catch (e) {
    return fallback;
  }
}

// Debounce function
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}